// Nick sanitising (protocol §9.2) and code-point counting (ui-flow §4.2: the nick field's limit is
// 15 code points, not UTF-16 units and not grapheme clusters, so "👍🏽" counts as 2).

/** Default nick limit in code points (mechanics Appendix A `nick.maxLength`). */
export const NICK_MAX_CODE_POINTS = 15;

/**
 * True for code points that `sanitizeNick` removes outright (protocol §9.2 step 2):
 * C0 and C1 controls, zero-width characters (U+200B–U+200F, U+2060, U+FEFF), bidi embeddings and
 * overrides (U+202A–U+202E) and bidi isolates (U+2066–U+2069). Lone surrogates are removed too:
 * they cannot be encoded as UTF-8 and would turn into U+FFFD on the wire.
 */
export function isStrippedCodePoint(cp: number): boolean {
  return (
    cp <= 0x1f ||
    (cp >= 0x7f && cp <= 0x9f) ||
    (cp >= 0x200b && cp <= 0x200f) ||
    cp === 0x2060 ||
    cp === 0xfeff ||
    (cp >= 0x202a && cp <= 0x202e) ||
    (cp >= 0x2066 && cp <= 0x2069) ||
    (cp >= 0xd800 && cp <= 0xdfff)
  );
}

/**
 * True for the whitespace that `sanitizeNick` collapses (the code points JS `\s` matches, minus the
 * control and zero-width ones that are stripped first).
 */
export function isNickWhitespace(cp: number): boolean {
  return (
    cp === 0x20 ||
    cp === 0xa0 ||
    cp === 0x1680 ||
    (cp >= 0x2000 && cp <= 0x200a) ||
    cp === 0x2028 ||
    cp === 0x2029 ||
    cp === 0x202f ||
    cp === 0x205f ||
    cp === 0x3000
  );
}

/**
 * Sanitises a nick (protocol §9.2 steps 1–4):
 * 1. Unicode NFC normalisation;
 * 2. remove control, zero-width and bidi-control characters (`isStrippedCodePoint`);
 * 3. collapse each run of whitespace to one U+0020 and trim both ends;
 * 4. cut to `maxCodePoints` code points. A cut never leaves a trailing space.
 *
 * Step 5 (the blocked-names list) is `isNickBlocked`, which the server applies to the result.
 * The result is at most `4 × maxCodePoints` UTF-8 bytes (60 at the default), inside the 64-byte
 * wire limit.
 */
export function sanitizeNick(raw: string, maxCodePoints: number = NICK_MAX_CODE_POINTS): string {
  const s = raw.normalize('NFC');
  let out = '';
  let count = 0;
  let pendingSpace = false;
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    if (isStrippedCodePoint(cp)) continue;
    if (isNickWhitespace(cp)) {
      pendingSpace = count > 0;
      continue;
    }
    if (pendingSpace) {
      // A space only goes in if the character after it fits too, so the result never ends in one.
      if (count + 2 > maxCodePoints) break;
      out += ' ';
      count++;
      pendingSpace = false;
    }
    if (count + 1 > maxCodePoints) break;
    out += ch;
    count++;
  }
  return out;
}

/** The number of Unicode code points in `s` (a lone surrogate counts as one). */
export function codePointLength(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) i++;
    }
    n++;
  }
  return n;
}

/** The first `maxCodePoints` code points of `s`; never splits a surrogate pair. */
export function truncateCodePoints(s: string, maxCodePoints: number): string {
  if (maxCodePoints <= 0) return '';
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    if (n === maxCodePoints) return s.slice(0, i);
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) i++;
    }
    n++;
  }
  return s;
}

/** The form nicks and blocked terms are compared in: NFC, lower case (locale-independent). */
export function nickMatchKey(s: string): string {
  return s.normalize('NFC').toLowerCase();
}

/**
 * Protocol §9.2 step 5: true if any non-empty blocked term is a case-insensitive substring of the
 * (already sanitised) nick. `blockedTerms` may be raw lines from `moderation.blockedNamesFile`;
 * they are trimmed and compared through `nickMatchKey`. For a large list, pre-normalise the terms
 * once and pass them with `preNormalised = true`.
 */
export function isNickBlocked(
  nick: string,
  blockedTerms: Iterable<string>,
  preNormalised = false,
): boolean {
  if (nick === '') return false;
  const key = nickMatchKey(nick);
  for (const raw of blockedTerms) {
    const term = preNormalised ? raw : nickMatchKey(raw.trim());
    if (term !== '' && key.includes(term)) return true;
  }
  return false;
}
